// Demo mode — axios response masking.
//
// The fetch patch in demoFetchPatch.js cannot see axios traffic: axios uses
// XMLHttpRequest in the browser, not fetch. This is the matching hook for the
// app's axios instances — attach it as a response interceptor and identities in
// the parsed body are swapped before any component reads them.
//
// Returns the response object itself when demo mode is off.

import { isDemoMode, maskObject, setKnownIdentities } from './demoMask';

const knownIdentities = new Set();

function collect(payload, depth = 0) {
  if (depth > 10 || !payload || typeof payload !== 'object') return;

  if (Array.isArray(payload)) {
    payload.forEach((item) => collect(item, depth + 1));
    return;
  }

  const first = payload.firstName || payload.first_name;
  const last = payload.lastName || payload.last_name;
  if (typeof first === 'string' && first.trim()) {
    knownIdentities.add(`${first} ${typeof last === 'string' ? last : ''}`.trim());
  }
  ['name', 'fullName', 'userName', 'memberName', 'displayName'].forEach((k) => {
    const v = payload[k];
    if (typeof v === 'string' && v.trim().length > 1 && v.trim().length < 60) {
      knownIdentities.add(v.trim());
    }
  });
  ['email', 'userEmail', 'emailAddress'].forEach((k) => {
    const v = payload[k];
    if (typeof v === 'string' && v.includes('@')) knownIdentities.add(v.trim());
  });

  Object.keys(payload).forEach((k) => {
    const v = payload[k];
    if (v && typeof v === 'object') collect(v, depth + 1);
  });
}

/**
 * Axios response interceptor. Safe to attach unconditionally.
 */
export function maskApiResponse(response) {
  try {
    if (!response || typeof response.data !== 'object' || response.data === null) {
      return response;
    }

    if (!isDemoMode()) return response;

    // Learn before masking, not after: prose in this very payload can name
    // someone this payload is also introducing, and rules registered
    // afterwards would arrive one response too late.
    const before = knownIdentities.size;
    collect(response.data);
    if (knownIdentities.size !== before) {
      setKnownIdentities(Array.from(knownIdentities));
    }

    response.data = maskObject(response.data);
    return response;
  } catch (_e) {
    // Masking must never break a request.
    return response;
  }
}

export default maskApiResponse;
